import { RadioButtonWithLabelOption, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { FormikRadioButtonGroup } from '~commonInfra/components/FormikRadioButtonGroup';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { componentRegistryEntry } from '~src/settings/general-settings/components/SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '~src/settings/general-settings/components/site-settings/ReactSiteSettingsForSiteAdmin';
import { useSelector } from '~src/settings/general-settings/utils';
import { learnMoreStyle, settingsRadioButtonItemHeight } from '~src/settings/styles/CommonStyles';

export const DATA_STORY_SECTION_TEST_ID = 'data-story-site-setting';
export const DATA_STORY_LEARN_MORE_TEST_ID = 'data-story-learn-more';
export const DATA_STORY_ENABLED_BUTTON_TEST_ID = 'data-story-enabled';
export const DATA_STORY_DISABLED_BUTTON_TEST_ID = 'data-story-disabled';


export type DataStorySectionProps = SiteSettingsForSiteAdminProps<'dataStoryEnabled'>;

/**
 * Settings section for management Data Story feature
 */
export function DataStorySection() {

  const buttonList = React.useMemo(() => getButtonListAndClassName(), []);

  return useShowSettingsSection() ? (
    <SettingsSection
      title={Messages.dataStorySiteSettingTitle()}
      testId={DATA_STORY_SECTION_TEST_ID}
    >
      <FormikRadioButtonGroup
        aria-label={Messages.dataStorySiteSettingTitle()}
        buttonList={buttonList}
        className={settingsRadioButtonItemHeight}
        name={'settings.dataStoryEnabled'}
      />
    </SettingsSection>
  ) : null;
}


function getButtonListAndClassName(): RadioButtonWithLabelOption<boolean>[] {

  const dataStoryEnabledButtonLabel = <>
    <div>{Messages.dataStoryEnabled()}</div>
    <div>{Messages.dataStoryEnabledDescription()}</div>
  </>;
  const dataStoryDisabledButtonLabel = <>
  <div>{Messages.dataStoryDisabled()}</div>
  <div>{Messages.dataStoryDisabledDescription()}
  <TextLinkWidget
      className={learnMoreStyle}
      href={HelpService.getServerHelpUrlByCategory('data-stories')}
      target='_blank'
      testId={DATA_STORY_LEARN_MORE_TEST_ID}
    >
    {Messages.learnMore()}
  </TextLinkWidget>
  </div>
</>;

  return[
    {
      value: true,
      label: dataStoryEnabledButtonLabel,
      testId: DATA_STORY_ENABLED_BUTTON_TEST_ID
    },
    {
      value: false,
      label: dataStoryDisabledButtonLabel,
      testId: DATA_STORY_DISABLED_BUTTON_TEST_ID
    }
  ];
}

/**
 * Display Data Story settings only if:
 * 1. The feature is enabled for a given site
 * 2. EITHER: The server is in an Online environment
 * 3. OR: The feature is available for on-prem servers
 */
function useShowSettingsSection(): boolean {
  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  if (FeatureFlags.dataStorySiteControl) {
    return isOnlineEnvironment || FeatureFlags.dataStoryAvailableOnPrem;
  }
  return false;
}

/* for registering the component in settings registry */
export const DataStorySectionSpecs = componentRegistryEntry({
  name: 'DataStorySection',
  component: DataStorySection
});
